import { useEffect, useRef, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { subscribeToPush } from '../../features/auth/services/pushService';
import { useFocusTrap } from '../../lib/useFocusTrap';

const STORAGE_KEY = 'ninpo.pushPrompt.dismissedAt';
const COOLDOWN_DAYS = 30;

const isPushSupported = (): boolean => {
  return typeof window !== 'undefined'
    && typeof Notification !== 'undefined'
    && 'serviceWorker' in navigator;
};

const wasRecentlyDismissed = (): boolean => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    if (!value) return false;
    const ts = Number(value);
    if (!Number.isFinite(ts)) return false;
    const ageMs = Date.now() - ts;
    return ageMs < COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

interface PushConsentPromptProps {
  /** Only show after this many ms on the current page. Default 8s. */
  delayMs?: number;
  /** Optional callback when the user explicitly enables. */
  onEnabled?: () => void;
}

/**
 * Pre-permission "soft" push prompt. Browsers count `Notification.requestPermission()`
 * dismissals against us forever, so we ask the user inside our own UI first
 * (where they can say no without burning the OS-level permission). We only
 * trigger the real `requestPermission()` after they tap "Yes".
 */
export default function PushConsentPrompt({ delayMs = 8_000, onEnabled }: PushConsentPromptProps) {
  const [visible, setVisible] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useFocusTrap(visible, panelRef, () => {
    try { window.localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* noop */ }
    setVisible(false);
  });

  useEffect(() => {
    if (!isPushSupported()) return;
    if (Notification.permission !== 'default') return;
    if (wasRecentlyDismissed()) return;
    const timer = window.setTimeout(() => setVisible(true), Math.max(0, delayMs));
    return () => window.clearTimeout(timer);
  }, [delayMs]);

  const dismiss = () => {
    try { window.localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* noop */ }
    setVisible(false);
  };

  const enable = async () => {
    try {
      const result = await Notification.requestPermission();
      if (result === 'granted') {
        await subscribeToPush().catch(() => null);
        if (typeof onEnabled === 'function') onEnabled();
      }
    } finally {
      dismiss();
    }
  };

  if (!visible) return null;

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="push-consent-title"
      className="fixed bottom-6 left-6 right-6 md:left-auto md:right-6 md:w-96 z-[11000] bg-zinc-900 text-white rounded-xl shadow-2xl border border-amber-400/30 p-4 flex items-start gap-3 outline-none"
    >
      <div className="bg-amber-400/20 text-amber-300 rounded-lg p-2 flex items-center justify-center">
        <Bell className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <h2 id="push-consent-title" className="text-sm font-black uppercase tracking-wide mb-1">
          Stay in the loop
        </h2>
        <p className="text-xs text-zinc-400 mb-3">
          Get notified when your driver picks up your order, is close by, and when promos drop.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={enable}
            className="bg-amber-400 hover:bg-amber-300 text-black text-xs font-black uppercase tracking-wide px-3 py-2 rounded transition-colors"
          >
            Yes, notify me
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-black uppercase tracking-wide px-3 py-2 rounded transition-colors"
          >
            Not now
          </button>
        </div>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={dismiss}
        className="p-3 rounded-2xl bg-ninpo-red/10 text-ninpo-red border border-ninpo-red/20 hover:bg-ninpo-red/20 transition-all duration-300"
      >
        <X className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
}
